import { create, type StoreApi, type UseBoundStore } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { AppState } from '@/store/rootTypes'
import { createStudentsSlice } from '@/store/slices/studentsSlice'
import { createProfileSlice } from '@/store/slices/profileSlice'
import { createProgressSlice } from '@/store/slices/progressSlice'
import { createAssessmentSlice } from '@/store/slices/assessmentSlice'
import { createGameSlice } from '@/store/slices/gameSlice'
import { createLearningSlice } from '@/store/slices/learningSlice'
import { createSettingsSlice } from '@/store/slices/settingsSlice'
import { createOutfitSlice } from '@/store/slices/outfitSlice'
import { createChallengeSlice } from '@/store/slices/challengeSlice'
import { createPretestSlice } from '@/store/slices/pretestSlice'
import { createFormativeSessionSlice } from '@/store/slices/formativeSessionSlice'
import { workspaceFromActiveState } from '@/store/workspace'
import { createStoreBootstrap, type StoreBootstrapResult } from '@/store/bootstrap'
import { mergePersistedState, migratePersistedState } from '@/store/migrations'
import { APP_STATE_STORAGE_KEY, APP_STATE_VERSION } from '@/repositories/app-state'
import { LocalKeyValueRepository } from '@/repositories/key-value'
import {
  createWorkspaceRepository,
  LegacyLocalStorageAdapter,
  WorkspaceCommitCoordinator,
  WorkspaceStateStorage,
  type WorkspaceRepository,
} from '@/repositories/workspace'

export interface CreateOpenELPStoreOptions {
  persistState?: boolean
  bootstrap?: StoreBootstrapResult
  workspaceSynchronization?: boolean
  workspaceRepository?: WorkspaceRepository
}

function createWorkspaceStateStorage(coordinator: WorkspaceCommitCoordinator, repository = createWorkspaceRepository()) {
  const legacy = new LegacyLocalStorageAdapter(new LocalKeyValueRepository(), 'openelp-store-v3')
  return new WorkspaceStateStorage(repository, legacy, coordinator)
}

function workspaceMatchesState(state: AppState) {
  if (!state.activeStudentId) return true
  const workspace = state.workspaces[state.activeStudentId]
  if (!workspace) return false
  const snapshot = workspaceFromActiveState(state)
  return (Object.keys(snapshot) as (keyof typeof snapshot)[])
    .every((key) => workspace[key] === snapshot[key])
}

function createStateCreator(bootstrap: StoreBootstrapResult, commitCoordinator?: WorkspaceCommitCoordinator) {
  return (set: StoreApi<AppState>['setState'], get: StoreApi<AppState>['getState']): AppState => {
    async function commitAction<T>(action: () => T): Promise<T> {
      if (!commitCoordinator) return action()
      await commitCoordinator.whenIdle()
      const previous = get()
      const result = action()
      try {
        await commitCoordinator.whenIdle()
        return result
      } catch (error) {
        commitCoordinator.discardNextWrite()
        set(previous)
        throw error
      }
    }

    return {
      ...bootstrap.initialState,
      ...createStudentsSlice(set, get, { commitCoordinator }),
      ...createProfileSlice(set, get),
      ...createSettingsSlice(set),
      ...createAssessmentSlice(set, get),
      ...createProgressSlice(set),
      ...createLearningSlice(set),
      ...createGameSlice(set, get),
      ...createOutfitSlice(set, get),
      ...createChallengeSlice(set, get),
      ...createPretestSlice(set, get),
      ...createFormativeSessionSlice(set),
      awaitWorkspaceCommit: () => commitCoordinator?.whenIdle() ?? Promise.resolve(),
      startFormativeTaskSessionAndCommit: (input) => commitAction(
        () => get().startFormativeTaskSession(input),
      ),
      submitFormativeTaskAnswerAndCommit: (input) => commitAction(
        () => get().submitFormativeTaskAnswer(input),
      ),
      completeFormativeTaskSessionAndCommit: (input) => commitAction(
        () => get().completeFormativeTaskSession(input),
      ),
      completeLearningTaskAndCommit: (input) => commitAction(
        () => get().completeLearningTask(input),
      ),
      completeLearningRouteTaskAndCommit: (input) => commitAction(
        () => get().completeLearningRouteTask(input),
      ),
      completeLessonPlayerAndCommit: (input) => commitAction(
        () => get().completeLessonPlayer(input),
      ),
      completeTaskAndCommit: async (taskId) => {
        if (get().completedTasks[taskId]) {
          await (commitCoordinator?.whenIdle() ?? Promise.resolve())
          return
        }
        await commitAction(() => get().completeTask(taskId))
      },
      addAttemptAndCommit: (attempt) => commitAction(
        () => get().addAttempt(attempt),
      ),
      recordDiagnosticAssessmentAnswerAndCommit: (attempt, wrongQuestion) => commitAction(
        () => get().recordDiagnosticAssessmentAnswer(attempt, wrongQuestion),
      ),
      recordClassroomAnswerAndCommit: (input) => commitAction(
        () => get().recordClassroomAnswer(input),
      ),
      recordLessonPlayerAnswerAndCommit: (input) => commitAction(
        () => get().recordLessonPlayerAnswer(input),
      ),
      markRewardClaimPlantGrantedAndCommit: (claimId, completedAt) => commitAction(
        () => get().markRewardClaimPlantGranted(claimId, completedAt),
      ),
      recordRewardClaimDeliveryFailureAndCommit: (input) => commitAction(
        () => get().recordRewardClaimDeliveryFailure(input),
      ),
      completeRewardClaimDeliveryAndCommit: (claimId, completedAt) => commitAction(
        () => get().completeRewardClaimDelivery(claimId, completedAt),
      ),
      completeRemediationCertificationAndCommit: (input) => commitAction(
        () => get().completeRemediationCertification(input),
      ),
      completeAITeacherCertificationAndCommit: (input) => commitAction(
        () => get().completeAITeacherCertification(input),
      ),
      saveAITeacherQuizSubmissionAndCommit: (session, trainingAttempt) => commitAction(
        () => get().saveAITeacherQuizSubmission(session, trainingAttempt),
      ),
      saveAITeacherAnalysisAndCommit: (session, explanation) => commitAction(
        () => get().saveAITeacherAnalysis(session, explanation),
      ),
      saveAITeacherWorkflowTransitionAndCommit: (input) => commitAction(
        () => get().saveAITeacherWorkflowTransition(input),
      ),
      completeGraduationAssessmentSessionAndCommit: (sessionId, record, reward) => commitAction(
        () => get().completeGraduationAssessmentSession(sessionId, record, reward),
      ),
      setPhaseAdvancementAndCommit: (result) => commitAction(
        () => get().setPhaseAdvancement(result),
      ),
    }
  }
}

function persistedStateProjection(state: AppState): AppState {
  return {
    ...state,
    aiTeacherResult: null,
    aiTeacherFingerprint: '',
    aiTeacherActive: false,
    phaseAdvancement: null,
    sessionStats: null,
    lastXpEvent: null,
    pendingAchievements: [],
  }
}

function attachWorkspaceSynchronization(store: UseBoundStore<StoreApi<AppState>>) {
  let syncing = false
  store.subscribe((state) => {
    if (syncing || !state.activeStudentId || workspaceMatchesState(state)) return
    syncing = true
    store.setState({
      workspaces: {
        ...state.workspaces,
        [state.activeStudentId]: workspaceFromActiveState(state),
      },
    })
    syncing = false
  })
}

export function createOpenELPStore(options: CreateOpenELPStoreOptions = {}) {
  const bootstrap = options.bootstrap ?? createStoreBootstrap()
  const commitCoordinator = options.persistState === false ? undefined : new WorkspaceCommitCoordinator()
  const workspaceStorage = commitCoordinator
    ? createWorkspaceStateStorage(commitCoordinator, options.workspaceRepository)
    : null
  const stateCreator = createStateCreator(bootstrap, commitCoordinator)
  const store = options.persistState === false
    ? create<AppState>()(stateCreator)
    : create<AppState>()(persist(stateCreator, {
        name: APP_STATE_STORAGE_KEY,
        version: APP_STATE_VERSION,
        storage: createJSONStorage(() => workspaceStorage!),
        partialize: persistedStateProjection,
        migrate: (persistedState, version) => migratePersistedState(
          persistedState,
          version,
          bootstrap.restoredLearningState,
        ),
        merge: (persistedState, currentState) => mergePersistedState(persistedState, currentState),
        onRehydrateStorage: () => () => undefined,
      }))

  if (options.workspaceSynchronization !== false) {
    attachWorkspaceSynchronization(store)
  }
  return store
}

// 唯一生产 Store 实例。测试使用 createOpenELPStore({ persistState: false }) 创建隔离实例。
export const useStore = createOpenELPStore()
